import { api } from "@/lib/api";
import type { ApiResponse, Course, PaginatedResponse } from "@learn-me/types";

interface CourseFilters {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  level?: string;
  isFree?: boolean;
  sort?: "recent" | "popular" | "price-asc" | "price-desc" | "title";
}

interface CourseWithDetails extends Course {
  chapters?: Array<{
    id: string;
    title: string;
    position: number;
    isFree: boolean;
    videoDuration?: number;
    isLocked?: boolean;
    userProgress?: {
      isCompleted: boolean;
    } | null;
  }>;
  isEnrolled?: boolean;
  userProgress?: number;
}

// API returns { courses: [...], pagination: {...} } but we normalize to { items: [...], pagination: {...} }
interface ApiCoursesResponse {
  courses: Course[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const coursesApi = {
  async list(filters: CourseFilters = {}): Promise<PaginatedResponse<Course>> {
    const params = new URLSearchParams();
    if (filters.page) params.set("page", filters.page.toString());
    if (filters.limit) params.set("limit", filters.limit.toString());
    if (filters.search) params.set("search", filters.search);
    if (filters.category) params.set("category", filters.category);
    if (filters.level) params.set("level", filters.level);
    if (filters.isFree !== undefined)
      params.set("isFree", filters.isFree.toString());
    if (filters.sort) params.set("sort", filters.sort);

    const response = await api.get<ApiResponse<ApiCoursesResponse>>(
      `/courses?${params}`,
    );
    const data = response.data.data!;
    // Normalize response to match PaginatedResponse type
    return {
      items: data.courses,
      pagination: data.pagination,
    };
  },

  async getBySlug(slug: string): Promise<CourseWithDetails> {
    const response = await api.get<ApiResponse<CourseWithDetails>>(
      `/courses/${slug}`,
    );
    return response.data.data!;
  },

  async getById(id: string): Promise<CourseWithDetails> {
    const response = await api.get<ApiResponse<CourseWithDetails>>(
      `/courses/id/${id}`,
    );
    return response.data.data!;
  },

  async enroll(courseId: string): Promise<void> {
    await api.post(`/courses/${courseId}/enroll`);
  },

  async getProgress(courseId: string): Promise<{
    progress: number;
    completedChapters: number;
    totalChapters: number;
  }> {
    const response = await api.get<
      ApiResponse<{
        progress: number;
        completedChapters: number;
        totalChapters: number;
      }>
    >(`/courses/${courseId}/progress`);
    return response.data.data!;
  },
};
